/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
   FaLink,
   FaPlus,
   FaCheck,
   FaExclamationTriangle,
} from 'react-icons/fa';

import CustomerNameSearch from '../../../../../components/CustomerNameSearch';
import { FieldError, FieldHint, SmallButton, ControlScope } from './styles';
import {
   formatAddressLine,
   formatCoordinate,
   geocodeBadgeForAddress,
   addressHasCoordinate,
} from './manualHelpers';

// ===========================================================================
// CustomerLinkedBlock — Modo A (vínculo). Busca um cliente cadastrado, lista
// seus endereços (radio) e mostra um preview read-only do endereço escolhido,
// com os dados espelhados do cadastro. O fetch (getCustomer/listAddresses) é do
// pai; aqui é apresentação + callbacks.
// ===========================================================================

const initialsOf = (name = '') =>
   name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase();

const NewCustomerRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 10px;
   margin-top: 10px;

   .ask {
      font-size: 12px;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   }
`;

const CustSel = styled.div`
   display: flex;
   align-items: center;
   gap: 11px;
   padding: 11px 13px;
   border: 1px solid ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.1)'};
   border-radius: 12px;
   background: #fafbfc;

   .av {
      flex-shrink: 0;
      width: 38px;
      height: 38px;
      border-radius: 999px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: 700;
      color: #fff;
      background: ${({ theme }) => theme?.colors?.primary ?? '#185FA5'};
   }
   .nm {
      font-size: 14px;
      font-weight: 700;
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   }
   .dc {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;
      margin-top: 2px;
      font-size: 12px;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   }
   .linktag {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 1px 7px;
      border-radius: 999px;
      font-size: 10.5px;
      font-weight: 700;
      color: ${({ theme }) => theme?.colors?.primary ?? '#185FA5'};
      background: ${({ theme }) => theme?.colors?.primarySoft ?? 'rgba(24,95,165,0.12)'};
   }
   .change {
      margin-left: auto;
   }
`;

const AddrHead = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 10px;
   margin: 16px 0 9px;

   .lbl {
      font-size: 12px;
      font-weight: 700;
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   }
   .req {
      color: ${({ theme }) => theme?.colors?.danger ?? '#c62828'};
      margin-left: 2px;
   }
`;

const AddrList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
`;

const AddrCard = styled.button`
   display: flex;
   align-items: flex-start;
   gap: 11px;
   width: 100%;
   text-align: left;
   padding: 11px 13px;
   border-radius: 12px;
   cursor: pointer;
   background: ${({ theme }) => theme?.colors?.surface ?? '#fff'};
   border: 1px solid
      ${({ $selected, theme }) =>
         ($selected
            ? theme?.colors?.primary ?? '#185FA5'
            : theme?.colors?.border ?? 'rgba(0,0,0,0.12)')};
   box-shadow: ${({ $selected }) =>
      ($selected ? '0 0 0 3px rgba(24,95,165,0.12)' : 'none')};
   transition:
      border-color 0.12s ease,
      box-shadow 0.12s ease;

   .radio {
      flex-shrink: 0;
      width: 16px;
      height: 16px;
      margin-top: 2px;
      border-radius: 999px;
      border: 2px solid
         ${({ $selected, theme }) =>
            ($selected
               ? theme?.colors?.primary ?? '#185FA5'
               : '#c7ccd4')};
      background: ${({ $selected, theme }) =>
         ($selected
            ? `radial-gradient(circle at center, ${theme?.colors?.primary ?? '#185FA5'} 0 4px, #fff 5px)`
            : '#fff')};
   }
   .body {
      min-width: 0;
      flex: 1;
   }
   .ln1 {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13.5px;
      font-weight: 600;
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   }
   .ln2 {
      margin-top: 2px;
      font-size: 12px;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   }
   .primary-tag {
      padding: 1px 7px;
      border-radius: 999px;
      font-size: 10px;
      font-weight: 700;
      color: ${({ theme }) => theme?.colors?.primary ?? '#185FA5'};
      background: ${({ theme }) => theme?.colors?.primarySoft ?? 'rgba(24,95,165,0.12)'};
   }
`;

const GeoBadge = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 4px;
   padding: 1px 7px;
   border-radius: 999px;
   font-size: 10px;
   font-weight: 700;
   color: ${({ $tone }) => ($tone === 'ok' ? '#1b7a44' : '#a85e29')};
   background: ${({ $tone }) =>
      ($tone === 'ok' ? 'rgba(27,122,68,0.12)' : 'rgba(232,132,60,0.14)')};
`;

const Preview = styled.div`
   margin-top: 12px;
   border: 1px solid ${({ theme }) => theme?.colors?.borderLight ?? 'rgba(0,0,0,0.06)'};
   border-radius: 12px;
   overflow: hidden;

   .pv-head {
      display: flex;
      align-items: center;
      gap: 7px;
      padding: 9px 13px;
      font-size: 11.5px;
      font-weight: 700;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
      background: #f7f8fa;
      border-bottom: 1px solid ${({ theme }) => theme?.colors?.borderLight ?? 'rgba(0,0,0,0.06)'};
   }
   .pv-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      padding: 13px;

      @media (max-width: 560px) {
         grid-template-columns: 1fr;
      }
   }
   .pv {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
   }
   .pv.full {
      grid-column: 1 / -1;
   }
   .k {
      font-size: 10.5px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      font-weight: 700;
      color: #9aa1ab;
   }
   .v {
      font-size: 13px;
      font-weight: 600;
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
      word-break: break-word;
   }
   .v.mono {
      font-variant-numeric: tabular-nums;
   }
`;

const NoGeoWarn = styled.div`
   display: flex;
   align-items: flex-start;
   gap: 8px;
   margin: 11px 13px 13px;
   padding: 9px 11px;
   border-radius: 10px;
   font-size: 12px;
   font-weight: 600;
   color: #a85e29;
   background: rgba(232, 132, 60, 0.1);
   border: 1px solid #f0ddcb;

   svg {
      flex-shrink: 0;
      margin-top: 1px;
   }
`;

export default function CustomerLinkedBlock({
   selectedCustomer = null,
   addresses = [],
   selectedAddressId = null,
   isLoadingAddresses = false,
   error = null,
   searchText = '',
   onSearchTextChange,
   onSelectCustomer,
   onClearCustomer,
   onSelectAddress,
   onOpenNewAddress,
   onOpenNewCustomer,
   disabled = false,
}) {
   // Sem cliente escolhido → busca + afordância "Novo cliente".
   if (!selectedCustomer) {
      return (
         <div>
            <ControlScope>
               <CustomerNameSearch
                  value={searchText}
                  onChange={onSearchTextChange}
                  onSelect={(customer) =>
                     customer && onSelectCustomer?.(customer)
                  }
                  placeholder="Nome ou CPF/CNPJ do cliente…"
                  disabled={disabled}
                  showHint
               />
            </ControlScope>
            <NewCustomerRow>
               <span className="ask">Cliente ainda não cadastrado?</span>
               <SmallButton
                  type="button"
                  $intent="link"
                  onClick={onOpenNewCustomer}
                  disabled={disabled}
               >
                  <FaPlus size={11} /> Novo cliente
               </SmallButton>
            </NewCustomerRow>
         </div>
      );
   }

   const c = selectedCustomer;
   const selectedAddress = addresses.find((a) => a.id === selectedAddressId) || null;

   let addressListContent;
   if (isLoadingAddresses) {
      addressListContent = <FieldHint>Carregando endereços…</FieldHint>;
   } else if (addresses.length === 0) {
      addressListContent = (
         <FieldHint>
            Este cliente ainda não tem endereço cadastrado. Use “Novo endereço”.
         </FieldHint>
      );
   } else {
      addressListContent = (
         <AddrList>
            {addresses.map((a) => {
               const badge = geocodeBadgeForAddress(a);
               return (
                  <AddrCard
                     key={a.id}
                     type="button"
                     $selected={a.id === selectedAddressId}
                     onClick={() => onSelectAddress?.(a.id)}
                     disabled={disabled}
                  >
                     <span className="radio" />
                     <div className="body">
                        <div className="ln1">
                           {formatAddressLine(a)}
                           {a.is_primary && (
                              <span className="primary-tag">Principal</span>
                           )}
                           {badge && (
                              <GeoBadge $tone={badge.tone}>
                                 {badge.tone === 'ok' ? (
                                    <FaCheck size={8} />
                                 ) : (
                                    <FaExclamationTriangle size={8} />
                                 )}
                                 {badge.label}
                              </GeoBadge>
                           )}
                        </div>
                        <div className="ln2">
                           {a.complement ? `${a.complement} · ` : ''}
                           {a.neighborhood ? `${a.neighborhood} · ` : ''}
                           {a.postal_code ? `CEP ${a.postal_code}` : ''}
                        </div>
                     </div>
                  </AddrCard>
               );
            })}
         </AddrList>
      );
   }

   return (
      <div>
         <CustSel>
            <span className="av">{initialsOf(c.name)}</span>
            <div>
               <div className="nm">{c.name}</div>
               <div className="dc">
                  <span className="linktag">
                     <FaLink size={9} /> vinculado
                  </span>
                  {c.doc ? <span>· {c.doc}</span> : null}
                  {c.phone ? <span>· {c.phone}</span> : null}
               </div>
            </div>
            <SmallButton
               type="button"
               className="change"
               onClick={onClearCustomer}
               disabled={disabled}
            >
               Trocar cliente
            </SmallButton>
         </CustSel>

         <AddrHead>
            <span className="lbl">
               Endereço de serviço
               <span className="req">*</span>
            </span>
            <SmallButton
               type="button"
               $intent="link"
               onClick={onOpenNewAddress}
               disabled={disabled}
            >
               <FaPlus size={12} /> Novo endereço
            </SmallButton>
         </AddrHead>

         {error && (
            <div style={{ marginBottom: 9 }}>
               <FieldError>
                  <FaExclamationTriangle size={12} /> {error}
               </FieldError>
            </div>
         )}

         {addressListContent}

         {selectedAddress && (
            <Preview>
               <div className="pv-head">
                  <FaCheck size={11} /> Dados espelhados do cadastro (somente leitura)
               </div>
               <div className="pv-grid">
                  <div className="pv">
                     <span className="k">Telefone</span>
                     <span className="v">{c.phone || '—'}</span>
                  </div>
                  <div className="pv">
                     <span className="k">Coordenada</span>
                     <span className="v mono">{formatCoordinate(selectedAddress)}</span>
                  </div>
                  <div className="pv full">
                     <span className="k">Endereço de serviço</span>
                     <span className="v">{formatAddressLine(selectedAddress)}</span>
                  </div>
                  <div className="pv">
                     <span className="k">Código Refiltek</span>
                     <span className="v">{c.refiltek_id || '—'}</span>
                  </div>
               </div>
               {!addressHasCoordinate(selectedAddress) && (
                  <NoGeoWarn>
                     <FaExclamationTriangle size={14} />
                     Endereço sem coordenada — precisará de ajuste manual de ponto
                     antes de otimizar.
                  </NoGeoWarn>
               )}
            </Preview>
         )}
      </div>
   );
}

CustomerLinkedBlock.propTypes = {
   // eslint-disable-next-line react/forbid-prop-types
   selectedCustomer: PropTypes.object,
   // eslint-disable-next-line react/forbid-prop-types
   addresses: PropTypes.array,
   selectedAddressId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   isLoadingAddresses: PropTypes.bool,
   error: PropTypes.string,
   searchText: PropTypes.string,
   onSearchTextChange: PropTypes.func,
   onSelectCustomer: PropTypes.func,
   onClearCustomer: PropTypes.func,
   onSelectAddress: PropTypes.func,
   onOpenNewAddress: PropTypes.func,
   onOpenNewCustomer: PropTypes.func,
   disabled: PropTypes.bool,
};
